import React, { useState, useEffect, useMemo } from "react";
import { Container, Card, CardBody, Spinner } from "reactstrap";
import { DashboardPageHeader, TableSkeleton } from "../../Components/Common/DashboardKit";
import api from "../../api";
import { inkFor } from "../../utils/themeColor";

const WhosInBuilding = () => {
    document.title = "Who's In The Building | SSW Dashboard";

    // Owner 2026-10-22: "employees need to see all employees in one page": the All view
    // joins the salaried and hourly lists, and the page shows everyone unless a page
    // size is picked.
    const [activeTab, setActiveTab] = useState("all");   // 'all' | 'salaried' | 'hourly'
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    // Owner 2026-10-12: the summary cards are the filter (Total / In Office / Out) and the
    // search is per column, like a grid, instead of one box for name or id.
    const [statusFilter, setStatusFilter] = useState("all");   // 'all' | 'IN' | 'OUT'
    // Owner 2026-10-15: "put remote whoever is remote, and office, so that everyone knows".
    const EMPTY_SEARCH = { empId: "", name: "", status: "", where: "", returningAt: "" };
    const whereOf = (e) => String(e.where ?? e.Where ?? "");
    const [colSearch, setColSearch] = useState(EMPTY_SEARCH);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(0);   // 0 = everyone on one page
    const PAGE_SIZE = pageSize > 0 ? pageSize : Number.MAX_SAFE_INTEGER;
    const pageStart = pageSize > 0 ? (page - 1) * pageSize : 0;
    const groupOf = (e) => (e.group === "Hourly" ? "Hourly" : e.group === "Salaried" ? "Salaried" : "");
    // Owner 2026-10-28: the words on screen are Exempt (salaried, IDL) and Non-Exempt (hourly, DL).
    const groupLabel = (g) => (g === "Hourly" ? "Non-Exempt" : g === "Salaried" ? "Exempt" : "");

    // ── Fetch ──────────────────────────────────────────────
    const fetchEmployees = async (type) => {
        setLoading(true);
        setStatusFilter("all");
        setColSearch(EMPTY_SEARCH);
        setPage(1);
        try {
            const list = (res) => (Array.isArray(res?.data) ? res.data : []);
            if (type === "all") {
                const [sal, hr] = await Promise.all([api.get("WhosInBuilding/salaried"), api.get("WhosInBuilding/hourly")]);
                const seen = new Set();
                const rows = [
                    ...list(sal).map((e) => ({ ...e, group: "Salaried" })),
                    ...list(hr).map((e) => ({ ...e, group: "Hourly" })),
                ].filter((e) => { const k = String(e.empId ?? "").trim(); if (!k || seen.has(k)) return false; seen.add(k); return true; });
                rows.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
                setEmployees(rows);
            } else {
                const res = await api.get(`WhosInBuilding/${type}`);
                setEmployees(list(res).map((e) => ({ ...e, group: type === "hourly" ? "Hourly" : "Salaried" })));
            }
        } catch (err) {
            console.error("Error fetching employees:", err);
            setEmployees([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchEmployees(activeTab); }, [activeTab]);

    // ── Filter + paginate ──────────────────────────────────
    const hasFilter = statusFilter !== "all" || Object.values(colSearch).some(v => v.trim() !== "");
    const filtered = useMemo(() => {
        const has = (value, needle) =>
            !needle.trim() || String(value ?? "").toLowerCase().includes(needle.trim().toLowerCase());
        return employees.filter(e => {
            const st = String(e.status || "").toUpperCase();
            if (statusFilter === "REMOTE") { if (st !== "IN" || whereOf(e) !== "Remote") return false; }
            else if (statusFilter !== "all" && st !== statusFilter) return false;
            return has(e.empId, colSearch.empId)
                && (has(e.name, colSearch.name) || (colSearch.name.trim() !== "" && has(groupOf(e), colSearch.name)))
                && has(e.status || "-", colSearch.status)
                && has(whereOf(e) || "-", colSearch.where)
                && has(e.returningAt || "N/A", colSearch.returningAt);
        });
    }, [employees, statusFilter, colSearch]);
    const clearFilters = () => { setStatusFilter("all"); setColSearch(EMPTY_SEARCH); setPage(1); };

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const paginated = filtered.slice(
        (page - 1) * PAGE_SIZE,
        page * PAGE_SIZE
    );

    // ── Summary counts ─────────────────────────────────────
    const inCount = employees.filter(e =>
        String(e.status || "").toUpperCase() === "IN"
    ).length;
    const outCount = employees.filter(e =>
        String(e.status || "").toUpperCase() === "OUT"
    ).length;
    const remoteCount = employees.filter(e =>
        String(e.status || "").toUpperCase() === "IN" && whereOf(e) === "Remote"
    ).length;

    // ── Styles ─────────────────────────────────────────────
    const btnBase = {
        padding: "5px 10px",
        borderRadius: "6px",
        border: "1px solid var(--color-border-tertiary)",
        background: "var(--color-background-primary)",
        color: "var(--color-text-primary)",
        cursor: "pointer",
        fontSize: "12px"
    };
    const btnDisabled = {
        ...btnBase,
        color: "var(--color-text-secondary)",
        cursor: "not-allowed"
    };
    const btnActive = {
        ...btnBase,
        background: "var(--ssw-primary-fill, #534AB7)",
        color: "#fff",
        fontWeight: 600,
        border: "1px solid #534AB7"
    };

    // ── Page numbers ───────────────────────────────────────
    const pageNumbers = Array.from(
        { length: totalPages }, (_, i) => i + 1
    )
        .filter(p =>
            p === 1 || p === totalPages || Math.abs(p - page) <= 1
        )
        .reduce((acc, p, i, arr) => {
            if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
            acc.push(p);
            return acc;
        }, []);

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-building-4-line"
                    eyebrow="Attendance"
                    title="Who's In The Building?"
                    subtitle="Live view of employees currently clocked in."
                    tone="blue"
                    breadcrumb={[
                        { label: "Dashboards", to: "/" },
                        { label: "Attendance", to: "/workflows?wf=pac" },
                        { label: "Who's In The Building" },
                    ]}
                />

                {/* ── Summary cards ──────────────────────── */}
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                    gap: "12px",
                    marginBottom: "20px"
                }}>
                    {[
                        {
                            label: "Total Employees",
                            value: employees.length,
                            color: "var(--ssw-ink-indigo, #534AB7)",
                            icon: "ri-team-line",
                            filter: "all"
                        },
                        {
                            label: "Clocked In",
                            value: inCount,
                            color: "var(--ssw-ink-green, #198754)",
                            icon: "ri-user-follow-line",
                            filter: "IN"
                        },
                        {
                            label: "Remote",
                            value: remoteCount,
                            color: "var(--ssw-ink-orange, #fd7e14)",
                            icon: "ri-home-wifi-line",
                            filter: "REMOTE"
                        },
                        {
                            label: "Out",
                            value: outCount,
                            color: "var(--ssw-ink-red, #dc3545)",
                            icon: "ri-user-unfollow-line",
                            filter: "OUT"
                        },
                    ].map(({ label, value, color, icon, filter }) => (
                        <div key={label}
                            role="button"
                            tabIndex={0}
                            title={filter === "all" ? "Show everyone" : `Show only ${label}`}
                            onClick={() => { setStatusFilter(filter); setPage(1); }}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setStatusFilter(filter); setPage(1); } }}
                            style={{
                                background: "var(--color-background-primary)",
                                border: "0.5px solid var(--color-border-tertiary)",
                                borderRadius: "10px",
                                padding: "14px 16px",
                                borderTop: `3px solid ${color}`,
                                cursor: "pointer",
                                boxShadow: statusFilter === filter ? `0 0 0 2px ${color}` : "none",
                                opacity: statusFilter === filter || statusFilter === "all" ? 1 : 0.7
                            }}>
                            <div style={{
                                fontSize: "12px",
                                color: "var(--color-text-secondary)",
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                                marginBottom: "6px"
                            }}>
                                <i className={icon} style={{ color }} />
                                {label}
                            </div>
                            <div style={{
                                fontSize: "24px",
                                fontWeight: 600,
                                color
                            }}>
                                {loading ? <Spinner size="sm" /> : value}
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Main card ──────────────────────────── */}
                <Card>
                    <CardBody>

                        {/* ── Tabs + Search ── */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "16px",
                            flexWrap: "wrap",
                            gap: "12px"
                        }}>
                            {/* Tabs */}
                            <div style={{
                                display: "flex",
                                border: "1px solid var(--color-border-tertiary)",
                                borderRadius: "8px",
                                overflow: "hidden"
                            }}>
                                {["all", "salaried", "hourly"].map((tab) => (
                                    <button
                                        key={tab}
                                        onClick={() => setActiveTab(tab)}
                                        style={{
                                            padding: "7px 20px",
                                            border: "none",
                                            fontSize: "13px",
                                            fontWeight: activeTab === tab ? 600 : 400,
                                            background: activeTab === tab
                                                ? "var(--ssw-primary-fill, #534AB7)"
                                                : "var(--color-background-primary)",
                                            color: activeTab === tab
                                                ? "#fff"
                                                : "var(--color-text-secondary)",
                                            cursor: "pointer",
                                            textTransform: "capitalize"
                                        }}
                                    >
                                        {tab === "all"
                                            ? <><i className="ri-group-line me-1" />All</>
                                            : tab === "salaried"
                                                ? <><i className="ri-briefcase-line me-1" />Exempt</>
                                                : <><i className="ri-time-line me-1" />Non-Exempt</>
                                        }
                                    </button>
                                ))}
                            </div>

                            {/* Clear filters + Refresh (the search is per column, in the table header) */}
                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "var(--color-text-secondary)", margin: 0 }}>
                                    Rows
                                    <select
                                        value={pageSize}
                                        onChange={(e) => { setPageSize(Number(e.target.value) || 0); setPage(1); }}
                                        aria-label="Rows per page"
                                        style={{
                                            padding: "5px 8px",
                                            borderRadius: "8px",
                                            border: "1px solid var(--color-border-tertiary)",
                                            background: "var(--color-background-primary)",
                                            color: "var(--color-text-primary)",
                                            fontSize: "13px"
                                        }}
                                    >
                                        <option value={0}>All on one page</option>
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                    </select>
                                </label>
                                {hasFilter && (
                                    <button
                                        onClick={clearFilters}
                                        style={{
                                            padding: "6px 14px",
                                            borderRadius: "8px",
                                            border: "1px solid var(--color-border-tertiary)",
                                            background: "var(--color-background-primary)",
                                            color: "var(--color-text-secondary)",
                                            cursor: "pointer",
                                            fontSize: "13px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "5px"
                                        }}
                                        title="Show everyone again"
                                    >
                                        <i className="ri-filter-off-line" />
                                        Clear filters
                                    </button>
                                )}
                                <button
                                    onClick={() => fetchEmployees(activeTab)}
                                    style={{
                                        padding: "6px 14px",
                                        borderRadius: "8px",
                                        border: "1px solid var(--color-border-tertiary)",
                                        background: "var(--color-background-primary)",
                                        color: "var(--color-text-secondary)",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px"
                                    }}
                                    title="Refresh"
                                >
                                    <i className="ri-refresh-line" />
                                    Refresh
                                </button>
                            </div>
                        </div>

                        {/* ── Table ── */}
                        {loading ? (
                            <div className="table-responsive">
                                <table className="table align-middle mb-0">
                                    <tbody><TableSkeleton rows={8} columns={4} /></tbody>
                                </table>
                            </div>
                        ) : (
                            <>
                                <table style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontSize: "13px"
                                }}>
                                    <thead>
                                        <tr style={{
                                            background: "var(--color-background-secondary)",
                                            borderBottom: "1px solid var(--color-border-tertiary)"
                                        }}>
                                            {["#", "Emp ID", "Name", "Status", "Where", "Returning At"].map(h => (
                                                <th key={h} style={{
                                                    padding: "10px 14px",
                                                    fontWeight: 600,
                                                    fontSize: "12px",
                                                    color: "var(--color-text-secondary)",
                                                    textAlign: h === "#" ? "center" : "left",
                                                    width: h === "#" ? "50px" : "auto"
                                                }}>
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                        {/* column search boxes (owner 2026-10-12) */}
                                        <tr style={{ borderBottom: "1px solid var(--color-border-tertiary)" }}>
                                            <th style={{ padding: "6px 14px" }} />
                                            {[
                                                { key: "empId", placeholder: "Emp ID", width: "110px" },
                                                { key: "name", placeholder: "Name", width: "220px" },
                                                { key: "status", placeholder: "IN / OUT", width: "110px" },
                                                { key: "where", placeholder: "Office / Remote", width: "130px" },
                                                { key: "returningAt", placeholder: "Returning at", width: "150px" },
                                            ].map(({ key, placeholder, width }) => (
                                                <th key={key} style={{ padding: "6px 14px", fontWeight: 400 }}>
                                                    <input
                                                        type="text"
                                                        value={colSearch[key]}
                                                        placeholder={`Search ${placeholder}`}
                                                        aria-label={`Search ${placeholder}`}
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            setColSearch((c) => ({ ...c, [key]: v }));
                                                            setPage(1);
                                                        }}
                                                        style={{
                                                            width,
                                                            maxWidth: "100%",
                                                            padding: "4px 8px",
                                                            borderRadius: "6px",
                                                            border: "1px solid var(--color-border-tertiary)",
                                                            background: "var(--color-background-primary)",
                                                            color: "var(--color-text-primary)",
                                                            fontSize: "12px"
                                                        }}
                                                    />
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.length === 0 && (
                                            <tr>
                                                <td colSpan={6} className="text-center py-5 text-muted">
                                                    <i className="ri-user-search-line fs-1 d-block mb-3" />
                                                    {hasFilter ? "No employees match the filters." : "No employees found."}
                                                </td>
                                            </tr>
                                        )}
                                        {paginated.map((emp, idx) => {
                                            const statusUp = String(emp.status || "").toUpperCase();
                                            const isIn = statusUp === "IN";
                                            const rowNum = pageStart + idx + 1;
                                            const where = whereOf(emp);
                                            const isRemote = where === "Remote";

                                            return (
                                                <tr key={emp.empId}
                                                    style={{
                                                        borderBottom: "0.5px solid var(--color-border-tertiary)",
                                                        background: idx % 2 === 0
                                                            ? "var(--color-background-primary)"
                                                            : "var(--color-background-secondary)"
                                                    }}
                                                >
                                                    {/* # */}
                                                    <td style={{
                                                        padding: "10px 14px",
                                                        textAlign: "center",
                                                        color: "var(--color-text-secondary)",
                                                        fontSize: "12px"
                                                    }}>
                                                        {rowNum}
                                                    </td>

                                                    {/* Emp ID */}
                                                    <td style={{ padding: "10px 14px" }}>
                                                        <span style={{
                                                            fontFamily: "monospace",
                                                            fontSize: "12px",
                                                            padding: "2px 8px",
                                                            borderRadius: "4px",
                                                            background: "var(--color-background-secondary)",
                                                            color: "var(--color-text-secondary)",
                                                            border: "0.5px solid var(--color-border-tertiary)"
                                                        }}>
                                                            {emp.empId}
                                                        </span>
                                                    </td>

                                                    {/* Name */}
                                                    <td style={{ padding: "10px 14px" }}>
                                                        <div style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "10px"
                                                        }}>
                                                            {/* Avatar initials */}
                                                            <div style={{
                                                                width: "32px",
                                                                height: "32px",
                                                                borderRadius: "50%",
                                                                background: isIn
                                                                    ? "#19875420"
                                                                    : "#dc354520",
                                                                color: isIn
                                                                    ? inkFor("#198754")
                                                                    : inkFor("#dc3545"),
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                fontSize: "11px",
                                                                fontWeight: 600,
                                                                flexShrink: 0
                                                            }}>
                                                                {emp.name
                                                                    ?.split(" ")
                                                                    .map(n => n[0])
                                                                    .slice(0, 2)
                                                                    .join("")
                                                                    .toUpperCase()
                                                                }
                                                            </div>
                                                            <span style={{
                                                                fontWeight: 500,
                                                                color: "var(--color-text-primary)"
                                                            }}>
                                                                {emp.name}
                                                            </span>
                                                            {activeTab === "all" && groupOf(emp) && (
                                                                <span
                                                                    title={groupOf(emp) === "Hourly" ? "Non-Exempt (clocks in on the factory clock or the dashboard)" : "Exempt"}
                                                                    style={{
                                                                        marginLeft: "8px",
                                                                        padding: "1px 7px",
                                                                        borderRadius: "10px",
                                                                        fontSize: "11px",
                                                                        border: "1px solid var(--color-border-tertiary)",
                                                                        color: "var(--color-text-secondary)",
                                                                        background: "var(--color-background-secondary)"
                                                                    }}
                                                                >
                                                                    {groupLabel(groupOf(emp))}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>

                                                    {/* Status */}
                                                    <td style={{ padding: "10px 14px" }}>
                                                        <span style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: "5px",
                                                            padding: "4px 12px",
                                                            borderRadius: "20px",
                                                            fontSize: "12px",
                                                            fontWeight: 600,
                                                            background: isIn
                                                                ? "#19875420"
                                                                : "#dc354520",
                                                            color: isIn
                                                                ? inkFor("#198754")
                                                                : inkFor("#dc3545"),
                                                        }}>
                                                            <span style={{
                                                                width: "6px",
                                                                height: "6px",
                                                                borderRadius: "50%",
                                                                background: isIn
                                                                    ? "#198754"
                                                                    : "#dc3545",
                                                                flexShrink: 0
                                                            }} />
                                                            {emp.status || "-"}
                                                        </span>
                                                    </td>

                                                    {/* Where (owner 2026-10-15): Office / Remote / Plant from today's punch */}
                                                    <td style={{ padding: "10px 14px" }}>
                                                        {where ? (
                                                            <span title={isRemote ? "Today's punch was marked remote" : "Today's punch came from the plant"}
                                                                style={{
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    gap: "5px",
                                                                    padding: "4px 12px",
                                                                    borderRadius: "20px",
                                                                    fontSize: "12px",
                                                                    fontWeight: 600,
                                                                    background: isRemote ? "#fd7e1420" : "#6c757d20",
                                                                    color: isRemote ? inkFor("#fd7e14") : "var(--color-text-secondary)",
                                                                }}>
                                                                <i className={isRemote ? "ri-home-wifi-line" : "ri-building-line"} />
                                                                {where}
                                                            </span>
                                                        ) : (
                                                            <span style={{ color: "var(--color-text-secondary)", fontSize: "12px" }}>-</span>
                                                        )}
                                                    </td>

                                                    {/* Returning At */}
                                                    <td style={{
                                                        padding: "10px 14px",
                                                        color: "var(--color-text-secondary)",
                                                        fontSize: "12px"
                                                    }}>
                                                        {emp.returningAt || "N/A"}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {/* ── Pagination ── */}
                                {totalPages > 1 && (
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        marginTop: "16px",
                                        paddingTop: "12px",
                                        borderTop: "0.5px solid var(--color-border-tertiary)"
                                    }}>
                                        <span style={{
                                            fontSize: "13px",
                                            color: "var(--color-text-secondary)"
                                        }}>
                                            Showing{" "}
                                            {pageStart + 1}–
                                            {Math.min(pageStart + PAGE_SIZE, filtered.length)}
                                            {" "}of {filtered.length} employees
                                        </span>

                                        <div style={{ display: "flex", gap: "4px" }}>
                                            <button
                                                onClick={() => setPage(1)}
                                                disabled={page === 1}
                                                style={page === 1 ? btnDisabled : btnBase}
                                            >
                                                <i className="ri-skip-back-line" />
                                            </button>

                                            <button
                                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                                disabled={page === 1}
                                                style={page === 1 ? btnDisabled : btnBase}
                                            >
                                                <i className="ri-arrow-left-s-line" />
                                                Prev
                                            </button>

                                            {pageNumbers.map((p, i) =>
                                                p === "..." ? (
                                                    <span key={`dots-${i}`} style={{
                                                        padding: "5px 6px",
                                                        fontSize: "12px",
                                                        color: "var(--color-text-secondary)"
                                                    }}>…</span>
                                                ) : (
                                                    <button
                                                        key={p}
                                                        onClick={() => setPage(p)}
                                                        style={page === p ? btnActive : btnBase}
                                                    >
                                                        {p}
                                                    </button>
                                                )
                                            )}

                                            <button
                                                onClick={() => setPage(p =>
                                                    Math.min(totalPages, p + 1)
                                                )}
                                                disabled={page === totalPages}
                                                style={page === totalPages
                                                    ? btnDisabled : btnBase}
                                            >
                                                Next
                                                <i className="ri-arrow-right-s-line ms-1" />
                                            </button>

                                            <button
                                                onClick={() => setPage(totalPages)}
                                                disabled={page === totalPages}
                                                style={page === totalPages
                                                    ? btnDisabled : btnBase}
                                            >
                                                <i className="ri-skip-forward-line" />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default WhosInBuilding;